import React, { useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Stack
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PricingPlans = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  // Vérifier si l'utilisateur possède déjà un forfait
  const hasPayed = localStorage.getItem('has_payed') === 'true';
  const currentStripeProductId = localStorage.getItem('stripe_product_id') || '';
  
  // Fonction pour vérifier si un plan est déjà possédé
  const isPlanOwned = (planId) => {
    if (!currentStripeProductId) return false;
    
    // Mapper les IDs de plans aux product IDs Stripe
    const planToProductIdMap = {
      'pro': 'prod_T9cd08zVJ5laxw',
      'pro-annual': 'prod_T9cdtvO5JzDFAI',
      'premium': 'prod_T9ceuCdQqAQ306', 
      'premium-annual': 'prod_T9cc02TtMZe4ye'
    };
    
    const planProductId = planToProductIdMap[planId] || planToProductIdMap[planId.replace('-annual', '')];
    return currentStripeProductId.includes(planProductId);
  };

  const plans = [
  {
    id: 'free',
    name: 'FREE',
    price: '0',
    period: 'month',
    description: 'To get started',
    features: [
      { included: true, text: 'Unlimited posts' },
      { included: true, text: '3 hook-words', icon: 'target' },
      { included: true, text: "Up to 10 opportunities/month", icon: 'mail', tooltip: "By opportunities, we mean the number of posts you can respond to" },
      { included: false, text: 'Profile badge' },
      { included: false, text: 'Personalized support' },
      { included: false, text: "Priority display" }
    ],
    annualPrice: '0',
    originalAnnualPrice: '0',
    color: '#6c757d',
    buttonText: 'Start for free',
    popular: false,
  },
  {
    id: 'pro',
    name: 'PRO',
    price: '19.90',
    period: 'month',
    description: 'For professionals',
    features: [
      { included: true, text: 'Unlimited posts' },
      { included: true, text: '10 hook-words', icon: 'target' },
      { included: true, text: "Up to 30 opportunities/month", icon: 'mail', tooltip: "By opportunities, we mean the number of posts you can respond to" },
      { included: true, text: 'Badge "PRO"' },
      { included: false, text: 'Personalized support' },
      { included: false, text: "Priority display" }
    ],
    annualPrice: '199',
    originalAnnualPrice: '238.80',
    color: '#007bff',
    buttonText: 'Get PRO',
    popular: true,
  },
  {
    id: 'premium',
    name: 'UNLIMITED',
    price: '69.90',
    period: 'month',
    description: 'For demanding users',
    features: [
      { included: true, text: 'Unlimited posts' },
      { included: true, text: 'Unlimited hook-words', icon: 'target' },
      { included: true, text: 'Unlimited opportunities', icon: 'fire' },
      { included: true, text: 'Badge "Premium"' },
      { included: true, text: "Hook-words optimization service", tooltip: "We provide an AI agent that analyzes your activity and products and automatically adds words to your profile" },
      { included: true, text: 'Priority in suggestions', icon: 'rocket' }
    ],
    annualPrice: '749',
    originalAnnualPrice: '838.80',
    color: '#ff9800',
    buttonText: 'Get UNLIMITED',
    popular: false,
  }
];

  const handleBillingPeriodChange = (event, newBillingPeriod) => {
    if (newBillingPeriod !== null) {
      setBillingPeriod(newBillingPeriod);
    }
  };

  const handleSubscribe = (planId) => {
    if(planId === 'free'){
      navigate(`/`);
    }
    else{
      const fullPlanId = billingPeriod === 'annual' ? `${planId}-annual` : planId;
      
      // Empêcher l'achat d'un forfait déjà possédé
      if (isPlanOwned(fullPlanId)) {
        alert(t('You already have this plan. Please choose a different plan or manage your subscription in your account settings.'));
        return;
      }
      
      navigate(`/dashboard/payment/checkout/${fullPlanId}`);
    }
  };

  return (
    <div id='content-page' className='content-page'>
      <Container>
        <Row>
          <Col sm="12">
            <Card className="position-relative inner-page-bg bg-primary" style={{ height: "150px" }}>
              <div className="inner-page-title">
                <h3 className="text-white">{t('SEEKS Pricing Plans')}</h3>
                <p className="text-white">{t('Options for every stage of your professional journey')}</p>
              </div>
            </Card>
          </Col>
          <Col sm="12" className="mt-4">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {t('Accelerate your success with SEEKS')}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2, maxWidth: '850px', mx: 'auto' }}>
                {t('Choose the plan that matches your professional needs and goals')}
              </Typography>

              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 4, mt: 2 }}>
                <ToggleButtonGroup
                  value={billingPeriod}
                  exclusive
                  onChange={handleBillingPeriodChange}
                  size="small"
                  color="primary"
                  aria-label="Billing period"
                >
                  <ToggleButton value="monthly" aria-label="monthly billing">
                    {t("Monthly")}
                  </ToggleButton>
                  <ToggleButton value="annual" aria-label="annual billing">
                    {t("Annual")} ({t("Save")})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Box>
            
            <Row className="justify-content-center">
              {plans.map((plan) => (
                <Col key={plan.id} lg={4} md={6} className="mb-4">
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      height: '100%',
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    {plan.popular && (
                      <Box 
                        sx={{
                          position: 'absolute',
                          top: 20,
                          right: -30,
                          transform: 'rotate(45deg)',
                          backgroundColor: plan.color,
                          color: 'white',
                          padding: '5px 40px',
                          zIndex: 1
                        }}
                      >
                        {t('Popular')}
                      </Box>
                    )}
                    <Box 
                      sx={{ 
                        bgcolor: plan.color,
                        color: 'white',
                        p: 3,
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="h5" component="h2" fontWeight="bold">
                        {t(plan.name)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                        {t(plan.description)}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 2 }}>
                        <Typography variant="h3" component="span" fontWeight="bold">
                          {plan.price === '0' ? t('FREE') : billingPeriod === 'annual' ? `${plan.annualPrice}€` : `${plan.price}€`}
                        </Typography>
                        {plan.price !== '0' && (
                          <Typography variant="subtitle1" component="span" sx={{ ml: 1 }}>
                            {billingPeriod === 'annual' ? `/${t('year')}` : `/${t(plan.period)}`}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Utiliser une hauteur minimum pour uniformiser les encarts */}
                      <Box sx={{ minHeight: '40px' }}>
                        {plan.price === '0' ? (
                          <Typography variant="body2" sx={{ color: 'white', opacity: 0.8, visibility: 'hidden' }}>
                            {t('Monthly/annual comparison')}
                          </Typography>
                        ) : billingPeriod === 'annual' ? (
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            <span style={{ textDecoration: 'none', marginRight: '5px', opacity: 1 }}>
                              {t('Save')} {Math.round((1 - (plan.annualPrice / (plan.price * 12))) * 100)}%
                            </span>
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            <span>
                              {plan.id === 'pro' ? t('Switch to annual for 17% savings') : t('Switch to annual for 11% savings')}
                            </span>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ p: 3 }}>
                      <List disablePadding>
                        {plan.features.map((feature, idx) => (
                          <React.Fragment key={idx}>
                            <ListItem disablePadding sx={{ py: 1 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {feature.included ? (
                                  feature.icon === 'target' ? (
                                    <span role="img" aria-label="target" style={{ color: 'green', fontSize: '1.2rem' }}>🎯</span>
                                  ) : feature.icon === 'mail' ? (
                                    <span role="img" aria-label="mail" style={{ color: '#0088cc', fontSize: '1.2rem' }}>📩</span>
                                  ) : feature.icon === 'fire' ? (
                                    <span role="img" aria-label="fire" style={{ color: 'orange', fontSize: '1.2rem' }}>🔥</span>
                                  ) : feature.icon === 'rocket' ? (
                                    <span role="img" aria-label="rocket" style={{ color: 'purple', fontSize: '1.2rem' }}>🚀</span>
                                  ) : (
                                    <CheckIcon sx={{ color: 'success.main' }} />
                                  )
                                ) : (
                                  <CloseIcon sx={{ color: 'text.disabled' }} />
                                )}
                              </ListItemIcon>
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <span>{t(feature.text)}</span>
                                    {feature.tooltip && (
                                      <Tooltip title={t(feature.tooltip)} arrow placement="top">
                                        <InfoIcon sx={{ ml: 0.5, fontSize: '0.9rem', color: 'primary.main', cursor: 'help' }} />
                                      </Tooltip>
                                    )}
                                  </Box>
                                } 
                                sx={{ 
                                  '& .MuiListItemText-primary': { 
                                    color: feature.included ? 'text.primary' : 'text.disabled' 
                                  } 
                                }}
                              />
                            </ListItem>
                            {idx < plan.features.length - 1 && <Divider component="li" variant="middle" />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Box>
                    
                    <Box sx={{ p: 3, pt: 0, textAlign: 'center' }}>
                      {(() => {
                        const fullPlanId = billingPeriod === 'annual' ? `${plan.id}-annual` : plan.id;
                        const isOwned = isPlanOwned(fullPlanId);
                        
                        return (
                          <Button 
                            variant={isOwned ? "outlined" : "contained"} 
                            size="large"
                            fullWidth
                            onClick={() => handleSubscribe(plan.id)}
                            disabled={isOwned}
                            sx={{ 
                              bgcolor: isOwned ? 'transparent' : plan.color,
                              borderColor: isOwned ? plan.color : 'transparent',
                              color: isOwned ? plan.color : 'white',
                              '&:hover': {
                                bgcolor: isOwned ? 'transparent' : plan.color,
                                filter: isOwned ? 'none' : 'brightness(90%)',
                                borderColor: isOwned ? plan.color : 'transparent'
                              },
                              '&.Mui-disabled': {
                                bgcolor: 'transparent',
                                borderColor: plan.color,
                                color: plan.color,
                                opacity: 0.7
                              },
                              py: 1.5,
                              mt: 2
                            }}
                          >
                            {isOwned ? t('Current Plan') : 
                             plan.price === '0' ? t(plan.buttonText) : 
                             billingPeriod === 'annual' ? t('Get annual subscription') : t(plan.buttonText)}
                          </Button>
                        );
                      })()} 
                    </Box>
                  </Paper>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PricingPlans;
